import { useEffect } from 'react';
import { useForm } from '@inertiajs/react';

interface UsernameModalProps {
    /** Whether the modal is open */
    isOpen: boolean;
    /** Callback to close the modal */
    onClose: () => void;
    /** Current username to pre-fill the input */
    currentUsername: string;
    /** Current map theme selection */
    mapTheme?: 'current' | 'parchment' | 'starfield';
    /** Callback when map theme changes */
    onMapThemeChange?: (theme: 'current' | 'parchment' | 'starfield') => void;
}

/**
 * Modal for editing the user's username.
 * Uses Inertia form to submit the update via PATCH request.
 */
export function UsernameModal({ isOpen, onClose, currentUsername, mapTheme = 'current', onMapThemeChange }: UsernameModalProps) {
    // Inertia form for submitting the username update
    const { data, setData, patch, processing, errors, reset, clearErrors } = useForm({
        username: currentUsername,
    });

    // Reset form data when modal opens or currentUsername changes
    useEffect(() => {
        if (isOpen) {
            setData('username', currentUsername);
            clearErrors();
        }
    }, [isOpen, currentUsername]);

    // Handle form submission
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        // If username didn't change, just close (theme changes are already persisted)
        if (data.username === currentUsername) {
            onClose();
            return;
        }
        patch('/user/username', {
            preserveScroll: true,
            onSuccess: () => {
                onClose();
            },
        });
    };

    // Handle cancel/close
    const handleClose = () => {
        setData('username', currentUsername);
        clearErrors();
        reset();
        onClose();
    };

    // Handle map theme change
    const handleThemeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const theme = e.target.value as 'current' | 'parchment' | 'starfield';
        try {
            localStorage.setItem('sellune.mapTheme', theme);
        } catch {}
        onMapThemeChange && onMapThemeChange(theme);
    };

    // Don't render if not open
    if (!isOpen) {
        return null;
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                onClick={handleClose}
            />

            {/* Modal content */}
            <div className="relative bg-slate-800 border border-slate-700 rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold text-white">Edit Username</h2>
                    <button
                        onClick={handleClose}
                        className="text-gray-400 hover:text-white transition-colors"
                        aria-label="Close modal"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <label htmlFor="username" className="block text-sm font-medium text-gray-300 mb-2">
                            Username
                        </label>
                        <input
                            type="text"
                            id="username"
                            value={data.username}
                            onChange={(e) => setData('username', e.target.value)}
                            className={`w-full px-4 py-2 bg-slate-700 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                                errors.username ? 'border-red-500' : 'border-slate-600'
                            }`}
                            placeholder="Enter your username"
                            minLength={3}
                            maxLength={30}
                            autoFocus
                        />
                        {errors.username && (
                            <p className="mt-2 text-sm text-red-400">{errors.username}</p>
                        )}
                        <p className="mt-2 text-xs text-gray-400">
                            3-30 characters. Letters, numbers, underscores, and hyphens only.
                        </p>
                    </div>

                    {/* Map Theme Selector */}
                    <div className="mb-4">
                        <label htmlFor="map-theme" className="block text-sm font-medium text-gray-300 mb-2">
                            Map Theme
                        </label>
                        <select
                            id="map-theme"
                            value={mapTheme}
                            onChange={handleThemeChange}
                            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                        >
                            <option value="current">Current Theme</option>
                            <option value="parchment">Parchment Map</option>
                            <option value="starfield">Starfield</option>
                        </select>
                        <p className="mt-2 text-xs text-gray-400">
                            Choose your preferred background for the hex map.
                        </p>
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={handleClose}
                            className="px-4 py-2 text-gray-300 hover:text-white transition-colors"
                            disabled={processing}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={processing}
                            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-800 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                        >
                            {processing ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
